<template>
    <div data-component="AmsSlider" class="relative -m-16">
        <component
            :is="listTag"
            ref="scrollableElement"
            :role="listRole"
            class="flex p-16 [&>*]:flex-shrink-0 overflow-scroll scrollbar-hide gap-24"
        >
            <slot />
        </component>

        <transition
            enter-to-class="opacity-100"
            enter-from-class="opacity-0"
            leave-to-class="opacity-0"
            leave-from-class="opacity-100"
        >
            <button
                v-if="isPrevButtonVisible"
                class="left-16 z-expanding-nav -translate-x-1/2 absolute p-8 top-1/2 -translate-y-1/2 rounded-full bg-color-greyscale-black transition duration-300 bg-opacity-50 hover:bg-opacity-100"
                type="button"
                aria-label="previous"
                @click="onPrevClick"
            >
                <MsChevron class="w-24 h-24 text-white rotate-90" />
            </button>
        </transition>

        <transition
            enter-to-class="opacity-100"
            enter-from-class="opacity-0"
            leave-to-class="opacity-0"
            leave-from-class="opacity-100"
            :duration="500"
        >
            <button
                v-if="isNextButtonVisible"
                class="right-16 z-expanding-nav translate-x-1/2 absolute p-8 top-1/2 -translate-y-1/2 rounded-full bg-color-greyscale-black transition duration-300 bg-opacity-50 hover:bg-opacity-100"
                type="button"
                aria-label="next"
                @click="onNextClick"
            >
                <MsChevron class="w-24 h-24 text-white -rotate-90" />
            </button>
        </transition>
    </div>
</template>

<script lang="ts" setup>
import { MsChevron } from '@sunrise/icons'
import { screenSizeDetection } from '@sunrise/shared'
import debounce from 'lodash-es/debounce'
import { PropType, onMounted, ref, computed } from 'vue'
import {
    calculateIntersection,
    isScrolledToEnd,
    isScrolledToStart,
    readjust,
    scrollToNext,
    scrollToPrevious,
} from './amsSliderHelper'

const props = defineProps({
    listTag: {
        type: String as PropType<keyof HTMLElementTagNameMap>,
        default: 'ul',
    },
})

const emit = defineEmits<{
    (e: 'scroll'): void
    (e: 'afterScroll'): void
    (e: 'prevClick', event: MouseEvent): void
    (e: 'nextClick', event: MouseEvent): void
}>()

const { isScreenM } = screenSizeDetection()
const scrollableElement = ref<HTMLDivElement>()
const isPrevButtonVisible = ref(false)
const isNextButtonVisible = ref(isScreenM.value ? true : false)

const listElements = ['ul', 'ol', 'li']
const listRole = computed(() => (!listElements.includes(props.listTag) ? 'list' : undefined))

defineExpose({
    scrollableElement,
    calculateIntersection: () => (scrollableElement.value ? calculateIntersection(scrollableElement.value) : null),
})

const attachEvents = () => {
    const afterScrollHandler = debounce(async () => {
        scrollableElement.value?.removeEventListener('scroll', afterScrollHandler)
        await readjust(scrollableElement.value as HTMLDivElement)
        scrollableElement.value?.addEventListener('scroll', afterScrollHandler)
        emit('afterScroll')
    }, 300)

    const visibleButtonsUpdateHandler = () => {
        updateStartAndEnd()
    }

    scrollableElement.value?.addEventListener('scroll', () => emit('scroll'))
    scrollableElement.value?.addEventListener('scroll', afterScrollHandler)
    scrollableElement.value?.addEventListener('scroll', visibleButtonsUpdateHandler)
}

const updateStartAndEnd = () => {
    if (!scrollableElement.value) {
        return
    }

    if (!isScreenM.value) {
        isPrevButtonVisible.value = false
        isNextButtonVisible.value = false
        return
    }

    isPrevButtonVisible.value = !isScrolledToStart(scrollableElement.value)
    isNextButtonVisible.value = !isScrolledToEnd(scrollableElement.value)
}

const onNextClick = (event: MouseEvent) => {
    scrollToNext(scrollableElement.value as HTMLDivElement)
    emit('nextClick', event)
}

const onPrevClick = (event: MouseEvent) => {
    scrollToPrevious(scrollableElement.value as HTMLDivElement)
    emit('prevClick', event)
}

onMounted(attachEvents)
onMounted(updateStartAndEnd)
</script>
